import { Component, computed, effect, EffectRef, ElementRef, inject, OnDestroy, OnInit, Signal, ViewChild } from '@angular/core';
import { ButtonComponent } from '@components/shared/button/button.component';
import { ButtonType } from '@models/enums/components/Button/ButtonType.enum';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { DvmService } from '@services/dvm.service';
import { Subscription } from 'rxjs';
import anime from 'animejs';
import { View3dDisplay } from '@models/types/components/View3dDisplay.type';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { UiService } from '@services/ui.service';

@Component({
  selector: 'app-fullscreen-toggle',
  standalone: true,
  imports: [ButtonComponent],
  templateUrl: './fullscreen-toggle.component.html',
  styleUrl: './fullscreen-toggle.component.css',
})
export class FullscreenToggleComponent implements OnInit, OnDestroy {

  @ViewChild('fullscreenBtn', { read: ElementRef })
  private readonly fullscreenBtn!: ElementRef<ButtonComponent>;

  // SERVICES
  private readonly dvmService:     DvmService              = inject(DvmService);

  // CONFIG
  private readonly uiconfig:       Signal<UiConfiguration>  = inject(UiService).uiConfig;
  private readonly view3dDisplay:  Signal<View3dDisplay> = computed((): View3dDisplay=> this.uiconfig().View3dDisplay);

  // GLOBAL STATE
  private readonly view:           Signal<AppViews | null> = this.dvmService.view;

  // BUTTON TYPE
  public readonly type:            ButtonType              = ButtonType.Pill;

  // STATE
  public showElement:              boolean                 = this.view() === AppViews.Pano;
  public isFullscreen:             boolean                 = false;
  public readonly suscriptions:    Subscription[]          = [];
  
  // EFFECTS
  private readonly onViewChange:   EffectRef               = effect((): void => {
    
    const view: AppViews | null = this.view(),
          show: boolean = view === AppViews.Pano && this.view3dDisplay().enabled && this.view3dDisplay().fullscreen;

    if(view === null) return;
    
    if (!this.showElement) {
      setTimeout((): void => this.animateFullscreen(show), 300);
    } 
    else {
      this.animateFullscreen(show);
    }
    
  })

  // LIFECYCLE HOOKS
  ngOnInit(): void {
    this.initComponent();
  }

  ngOnDestroy(): void {
    this.destroyComponent();
  }

  // METHODS
  public toggleFullscreen(): void {
    
    if(this.view() === AppViews.Pano) {
      this.dvmService.viewer3d.toggleFullscreen();
    }

  }

  private initComponent(): void {

    const viewer3d = this.dvmService.viewer3d;

    viewer3d.waitInitialize().subscribe((): void => {
      this.suscriptions.push(
        
        // Full screen enabled
        viewer3d.getObservable('fullscreen_enabled').subscribe((): boolean => this.isFullscreen = true),
        
        // Full screen disabled
        viewer3d.getObservable('fullscreen_disabled').subscribe((): boolean => this.isFullscreen = false),

      )
    })

  }

  private destroyComponent(): void {
    this.suscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  private animateFullscreen(show: boolean): void {

    anime({
      begin:    (): void => { if(show && !this.showElement) this.showElement = true },
      complete: (): void => { if(!show && this.showElement) this.showElement = false },
      targets:  this.fullscreenBtn.nativeElement,
      opacity:  show ? 1 : 0,
      duration: 300,
      easing:   'easeInOutQuad',
    })

  }

}
